import React, { useState, useEffect, useCallback } from 'react';
import { getTiposCreditoPaginados } from '../services/tipoCreditoService';
import { Link, useNavigate } from 'react-router-dom';
import { PlusCircle, ArrowLeft, RefreshCw, AlertCircle, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, FileText } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export default function TiposCreditoList() {
    const [tipos, setTipos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination & Search States
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const navigate = useNavigate();
    const { token } = useAuth();

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const cargarTipos = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getTiposCreditoPaginados(token, page, pageSize, debouncedSearch);
            setTipos(data.content);
            setTotalPages(data.totalPages);
            setTotalElements(data.totalElements);
            setError(null);
        } catch (error) {
            console.error('Error al cargar tipos de crédito:', error);
            setError('No se pudieron cargar los tipos de crédito.');
            toast.error('Error al cargar los datos');
        } finally {
            setLoading(false);
        }
    }, [token, page, pageSize, debouncedSearch]);

    useEffect(() => {
        cargarTipos();
    }, [cargarTipos]);

    useEffect(() => {
        setPage(0);
    }, [debouncedSearch, pageSize]);

    const startItem = totalElements === 0 ? 0 : page * pageSize + 1;
    const endItem = Math.min((page + 1) * pageSize, totalElements);

    return (
        <div className="max-w-[98%] mx-auto bg-white shadow-sm border border-gray-200 mb-10 overflow-hidden rounded-sm">
            <Toaster position="top-right" />

            {/* Header Enterprise Style (Slate) - Idéntico a Profesiones */}
            <div className="bg-slate-700 p-3 flex justify-between items-center text-white">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/parametrizacion#financieros')}
                        className="p-1 hover:bg-slate-600 rounded transition-colors text-slate-300 hover:text-white"
                        title="Volver"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h1 className="text-lg font-semibold tracking-tight uppercase">Mantenimiento: Tipos de Crédito (Referencial Contable)</h1>
                </div>

                <Link
                    to="/parametrizacion/tipos-credito/nuevo"
                    className="flex items-center gap-1.5 transition-all rounded-sm px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white shadow-sm text-[0.85rem] font-bold uppercase"
                >
                    <PlusCircle className="w-4 h-4" />
                    NUEVO TIPO
                </Link>
            </div>

            {/* Controles Compactos */}
            <div className="p-3 bg-gray-50 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4">
                <div className="flex items-center gap-4">
                    <div className="relative group">
                        <input
                            type="text"
                            placeholder="Buscar por nombre..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="bg-white border border-gray-300 pl-8 pr-3 py-1.5 w-64 focus:ring-1 focus:ring-blue-500 outline-none text-[0.875rem] rounded-sm shadow-sm"
                        />
                        <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5 group-focus-within:text-blue-500" />
                    </div>

                    <div className="flex items-center gap-2">
                        <label className="text-xs font-bold text-gray-500 uppercase">Mostrar:</label>
                        <select
                            value={pageSize}
                            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                            className="bg-white border border-gray-300 px-2 py-1 text-[0.875rem] font-medium outline-none rounded-sm shadow-sm"
                            disabled={loading}
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                        </select>
                    </div>
                </div>

                <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                    {loading && <RefreshCw className="w-3 h-3 animate-spin text-blue-500" />}
                    VISTA DE AUDITORÍA CONTABLE
                </div>
            </div>

            {/* Grilla Principal con Scroll Horizontal y Sticky Columns */}
            <div className="overflow-x-auto relative">
                <table className="min-w-full text-left border-collapse table-fixed">
                    <thead>
                        <tr className="bg-[#f8fafc] border-b-2 border-slate-200 text-gray-700 text-[10px] font-bold uppercase tracking-wider">
                            <th className="px-3 py-2 w-16 text-center border-r border-gray-200 sticky left-0 bg-[#f8fafc] z-10">ID</th>
                            <th className="px-4 py-2 w-64 border-r border-gray-200">Tipo de Crédito / Alcance</th>
                            <th className="px-4 py-2 w-56 border-r border-gray-200">Cta. Capital (Debe)</th>
                            <th className="px-4 py-2 w-56 border-r border-gray-200">Cta. Interés (Haber)</th>
                            <th className="px-3 py-2 w-24 text-center border-r border-gray-200">Tasa Ref.</th>
                            <th className="px-3 py-2 w-32 text-center sticky right-0 bg-[#f8fafc] shadow-[-4px_0_10px_-4px_rgba(0,0,0,0.1)] border-l border-gray-200 z-10">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="text-[12px] text-gray-600">
                        {loading && tipos.length === 0 ? (
                            <tr><td colSpan="6" className="px-4 py-8 text-center opacity-50 font-bold uppercase italic border-b border-gray-200">Sincronizando maestros...</td></tr>
                        ) : tipos.length === 0 ? (
                            <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-400 font-bold uppercase italic border-b border-gray-200">No hay datos para la consulta.</td></tr>
                        ) : (
                            tipos.map((t) => (
                                <tr key={t.id} className="border-b border-gray-100 hover:bg-[#f1f5f9] transition-colors group">
                                    <td className="px-3 py-2 text-center font-mono text-[11px] text-gray-400 border-r border-gray-100 sticky left-0 bg-white group-hover:bg-[#f1f5f9] z-10">{t.id}</td>
                                    <td className="px-4 py-2 border-r border-gray-100">
                                        <div className="font-bold text-gray-800 uppercase tracking-tight">{t.nombre}</div>
                                        <div className="text-[9px] text-gray-400 truncate max-w-[220px] italic">{t.descripcion || 'Sin descripción adicional'}</div>
                                    </td>
                                    <td className="px-4 py-2 border-r border-gray-100">
                                        <div className="flex items-center gap-2">
                                            <FileText className="w-3 h-3 text-blue-500 flex-shrink-0" />
                                            <div className="truncate">
                                                <div className="text-[11px] font-black text-blue-700 leading-none">{t.cuentaContableCapitalCodigo}</div>
                                                <div className="text-[10px] text-gray-500 uppercase font-medium mt-0.5 truncate">{t.cuentaContableCapitalNombre}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-2 border-r border-gray-100">
                                        <div className="flex items-center gap-2">
                                            <FileText className="w-3 h-3 text-emerald-600 flex-shrink-0" />
                                            <div className="truncate">
                                                <div className="text-[11px] font-black text-emerald-700 leading-none">{t.cuentaContableInteresCodigo}</div>
                                                <div className="text-[10px] text-gray-500 uppercase font-medium mt-0.5 truncate">{t.cuentaContableInteresNombre}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-center font-bold text-slate-700 border-r border-gray-100 italic">
                                        {Number(t.tasaReferencia).toFixed(2)}%
                                    </td>
                                    <td className="px-3 py-2 text-center sticky right-0 bg-white shadow-[-4px_0_10px_-4px_rgba(0,0,0,0.1)] border-l border-gray-200 group-hover:bg-[#f1f5f9] z-10">
                                        <div className="flex justify-center items-center gap-2 font-semibold text-xs">
                                            <Link
                                                to={`/parametrizacion/tipos-credito/${t.id}/editar`}
                                                className="text-blue-600 hover:text-blue-800 transition-colors"
                                                title="Editar"
                                            >
                                                EDITAR
                                            </Link>
                                            <span className="text-gray-300">|</span>
                                            <button
                                                className="text-red-600 hover:text-red-800 transition-colors"
                                                title="Eliminar"
                                                onClick={() => { if (window.confirm('¿Eliminar este tipo de crédito?')) {/* Implementar delete si es necesario */ } }}
                                            >
                                                ELIMINAR
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Paginación Enterprise */}
            <div className="p-3 bg-gray-50 flex flex-col sm:flex-row justify-between items-center gap-2 border-t border-gray-200 text-[0.875rem] text-gray-500 uppercase font-bold">
                <div>
                    Mostrando <span className="text-gray-700">{startItem} a {endItem}</span> de <span className="text-gray-700">{totalElements}</span> registros
                </div>

                <div className="flex items-center gap-1">
                    <button onClick={() => setPage(0)} disabled={page === 0 || loading} className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-30 rounded-sm italic text-xs">P Primero</button>
                    <button onClick={() => setPage(page - 1)} disabled={page === 0 || loading} className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-30 rounded-sm"><ChevronLeft className="w-4 h-4" /></button>

                    <div className="px-4 py-1 border border-blue-600 bg-blue-600 text-white font-bold text-xs rounded-sm">
                        {page + 1}
                    </div>
                    <span className="text-[10px] font-bold text-gray-400 px-1 uppercase italic ml-1">de {totalPages || 1}</span>

                    <button onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1 || loading} className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-30 rounded-sm"><ChevronRight className="w-4 h-4" /></button>
                    <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1 || totalPages === 0 || loading} className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-30 rounded-sm italic text-xs">U Último</button>
                </div>
            </div>
        </div>
    );
}
